// Admin Email settings page. Configures the two emails an inquiry sends:
//   - the sales-team notification recipient (name + email), and
//   - the customer confirmation (from address, subject, and rich-text
//     intro/outro copy), plus the ops address alerted when sending fails.
//
// All values live in portal_settings (admin-only keys) and are saved through
// the shared admin-settings slice. Empty fields fall back to the server env
// config, so leaving a field blank preserves the prior behavior.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Button, Paper, TextField, Stack, Divider,
} from '@mui/material';
import {
  fetchAdminSettings, saveAdminSettings, clearSaveError,
} from '../../store/admin-settings-slice.js';
import { fetchPublicConfig } from '../../store/settings-slice.js';
import { showSnackbar } from '../../store/snackbar-slice.js';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard.js';
import RichTextEditor from '../../components/admin/RichTextEditor.jsx';

// The settings keys this page owns. Kept in one list so the payload, the dirty
// diff, and the form seeding all stay in sync.
const EMAIL_KEYS = [
  'email_from_address',
  'email_from_name',
  'sales_rep_name',
  'sales_rep_email',
  'tech_support_email',
  'confirmation_email_subject',
  'confirmation_email_intro',
  'confirmation_email_outro',
  'thank_you_title',
  'thank_you_body',
];

const RTF_KEYS = new Set(['confirmation_email_intro', 'confirmation_email_outro']);

// Builds the normalized save payload (and the value diffed against) so trimmed
// whitespace never looks dirty. RTF keys are JSON strings — left untouched.
const buildPayload = (form) => {
  const out = {};
  EMAIL_KEYS.forEach((key) => {
    const raw = form[key] ?? '';
    out[key] = RTF_KEYS.has(key) ? raw : String(raw).trim();
  });
  return out;
};

const AdminEmailPage = () => {
  const dispatch = useDispatch();
  const {
    settings, loading, error, saving, saveError,
  } = useSelector((s) => s.adminSettings);
  const [form, setForm] = useState(null);

  useEffect(() => { dispatch(fetchAdminSettings()); }, [dispatch]);

  useEffect(() => {
    if (settings) setForm(structuredClone(settings));
  }, [settings]);

  const dirty = useMemo(() => {
    if (!settings || !form) return false;
    return JSON.stringify(buildPayload(form)) !== JSON.stringify(buildPayload(settings));
  }, [form, settings]);

  useUnsavedChangesGuard(dirty);

  if (loading && !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading email settings" />
      </Box>
    );
  }

  if (error && !form) return <Alert severity="error">{error}</Alert>;
  if (!form) return null;

  const setField = (key, value) => {
    if (saveError) dispatch(clearSaveError());
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const result = await dispatch(saveAdminSettings(buildPayload(form)));
    if (saveAdminSettings.fulfilled.match(result)) {
      // Confirmation-page keys are public, so refresh the public config the
      // live thank-you page renders from.
      dispatch(fetchPublicConfig());
      dispatch(showSnackbar({ message: 'Email settings saved.', severity: 'success' }));
    }
  };

  const handleReset = () => setForm(structuredClone(settings));

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap',
      }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the sales notification and the customer confirmation email.
            Leave a field blank to use the server default.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {dirty && <Typography variant="body2" color="text.secondary">Unsaved changes</Typography>}
          <Button onClick={handleReset} disabled={!dirty || saving}>Reset</Button>
          <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Sending
        </Typography>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Send from address"
              value={form.email_from_address ?? ''}
              onChange={(e) => setField('email_from_address', e.target.value)}
              helperText="Must be an @sanborn.com address. Blank uses the server default."
              fullWidth
              sx={{ flex: '1 1 280px' }}
            />
            <TextField
              label="From name (optional)"
              value={form.email_from_name ?? ''}
              onChange={(e) => setField('email_from_name', e.target.value)}
              helperText="Shown alongside the address, e.g. Sanborn Sales."
              fullWidth
              sx={{ flex: '1 1 220px' }}
            />
          </Box>
          <TextField
            label="Technical support address"
            value={form.tech_support_email ?? ''}
            onChange={(e) => setField('tech_support_email', e.target.value)}
            helperText="Internal address alerted when an inquiry email fails to send. Blank uses the server default."
            fullWidth
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Sales representative
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Receives inquiry notifications; the name signs the customer
          confirmation email.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Sales rep name"
            value={form.sales_rep_name ?? ''}
            onChange={(e) => setField('sales_rep_name', e.target.value)}
            fullWidth
            sx={{ flex: '1 1 220px' }}
          />
          <TextField
            label="Sales rep email"
            value={form.sales_rep_email ?? ''}
            onChange={(e) => setField('sales_rep_email', e.target.value)}
            helperText="Blank uses the server default recipient."
            fullWidth
            sx={{ flex: '1 1 280px' }}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Customer confirmation email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sent to the person who submits an inquiry. The project summary,
          selected products, and estimate are inserted automatically between the
          intro and outro.
        </Typography>
        <Stack spacing={3}>
          <TextField
            label="Subject"
            value={form.confirmation_email_subject ?? ''}
            onChange={(e) => setField('confirmation_email_subject', e.target.value)}
            fullWidth
          />
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Intro</Typography>
          </Divider>
          <RichTextEditor
            label="Intro text"
            ariaLabel="Confirmation email intro"
            value={form.confirmation_email_intro ?? ''}
            onChange={(v) => setField('confirmation_email_intro', v)}
            placeholder="Thank you for your interest…"
          />
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Outro</Typography>
          </Divider>
          <RichTextEditor
            label="Outro text"
            ariaLabel="Confirmation email outro"
            value={form.confirmation_email_outro ?? ''}
            onChange={(v) => setField('confirmation_email_outro', v)}
            placeholder="Thank you for exploring the program…"
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Confirmation page
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The on-screen page shown after an inquiry is submitted. Include any
          follow-up contact email directly in the body text.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Heading"
            value={form.thank_you_title ?? ''}
            onChange={(e) => setField('thank_you_title', e.target.value)}
            fullWidth
          />
          <TextField
            label="Body text"
            value={form.thank_you_body ?? ''}
            onChange={(e) => setField('thank_you_body', e.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default AdminEmailPage;
